import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Star, Brain } from "lucide-react";
import AnimatedHeading from "./AnimatedHeading";
import Button from "../../../components/ui/Button";
import fibiLogo from "../../../../../images/web/fibi-logo.svg";
const Hero: React.FC = () => {

  return (
    <section
      className="bg-white py-16 sm:py-24 relative overflow-hidden flex items-center min-h-screen"
    >
      {/* Background Radial Glow */}
      <div className="absolute top-0 right-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-ea/20 rounded-full blur-[128px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[45%] h-[45%] bg-blue-ea/30 rounded-full blur-[128px]" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-center">
          {/* Left Column: Headline and CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 flex flex-col items-start text-left"
          >
            {/* Animated Headline cycling visa types */}
            <div className="w-full">
              <AnimatedHeading />
            </div>

            {/* Sub-headline */}
            <p className="mt-6 leading-relaxed text-dark-6b max-w-2xl font-medium">
              Meet FiBi: Your Personalized 24/7 Interview Trainer. Our AI
              generates realistic consular interview questions with both text
              and voice simulations to help you speak with confidence. Practice
              makes perfect!
            </p>

            {/* Side-by-side CTA buttons with deep-link pricing */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button
                label="Start Preparing Today"
                onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                variant="fibi"
                size="large"
                endIcon={<ArrowRight className="w-4 h-4" />}
                iconHoverStyle="scale"
              />
              <Button
                label="Explore Business Plans"
                onClick={() => {
                  document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="secondary"
                size="large"
                endIcon={<ArrowRight className="w-4 h-4" />}
                iconHoverStyle="rotate"
              />
            </div>
          </motion.div>

          {/* Right Column: Minimalist Premium Dashboard & Circular Gauge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-6 relative flex justify-center items-center h-[420px] sm:h-[480px]"
          >
            {/* Minimalist Dashboard Card */}
            <div className="w-[85%] sm:w-[75%] p-4 sm:p-5 rounded-3xl border border-border bg-white shadow-card-lg relative flex flex-col items-center">
              <div className="w-full flex justify-between items-center border-b border-border pb-3 mb-4">
                <div className="flex items-center">
                  <img
                    src={fibiLogo}
                    alt="FiBi Identity Logo"
                    className="h-12 sm:h-14 lg:h-16 w-auto object-contain"
                  />
                </div>
                <div className="flex gap-1.5">
                  {[1, 2, 3].map((dot) => (
                    <div
                      key={dot}
                      className="w-2 h-2 rounded-full bg-surface-alt"
                    />
                  ))}
                </div>
              </div>

              {/* Central Element: Large Circular SVG Gauge */}
              <div className="relative w-44 h-44 flex items-center justify-center">
                <svg className="absolute w-full h-full -rotate-90">
                  {/* FiBi brand gradient (blue → cyan) for the active gauge arc */}
                  <defs>
                    <linearGradient
                      id="fibiGaugeGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#0072FF" />
                      <stop offset="100%" stopColor="#00FFFF" />
                    </linearGradient>
                  </defs>
                  {/* Gauge background circle */}
                  <circle
                    cx="88"
                    cy="88"
                    r="76"
                    className="stroke-surface-alt"
                    strokeWidth="12"
                    fill="transparent"
                  />
                  {/* Active animated stroke gauge */}
                  <motion.circle
                    cx="88"
                    cy="88"
                    r="76"
                    stroke="url(#fibiGaugeGradient)"
                    strokeLinecap="round"
                    strokeWidth="12"
                    fill="transparent"
                    strokeDasharray={477.5}
                    initial={{ strokeDashoffset: 477.5 }}
                    animate={{ strokeDashoffset: 477.5 - (477.5 * 74) / 100 }}
                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.4 }}
                  />
                </svg>

                {/* Score numbers inside circular gauge */}
                <div className="text-center z-10">
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                    className="text-4xl font-light font-lato text-black-1a leading-none block"
                  >
                    74%
                  </motion.span>
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider block mt-1">
                    Readiness Score
                  </span>
                </div>
              </div>

              {/* Minor stats underneath gauge */}
              <div className="mt-5 grid grid-cols-2 gap-3 w-full text-center">
                <div className="p-3 bg-surface-alt rounded-2xl border border-border">
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider block">
                    Pace
                  </span>
                  <span className="text-sm font-bold text-black-1a block mt-0.5">
                    Optimal
                  </span>
                </div>
                <div className="p-3 bg-surface-alt rounded-2xl border border-border">
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider block">
                    Clarity
                  </span>
                  <span className="text-sm font-bold text-black-1a block mt-0.5">
                    Good
                  </span>
                </div>
              </div>
            </div>

            {/* Float Bubbles pointing to interview questions */}
            {/* Bubble 1: Top-Left (translated upwards to leave room for FiBi header) */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute left-0 sm:left-2 -top-4 z-20 bg-white border border-border rounded-2xl shadow-card px-4 py-3 max-w-[180px] flex gap-2 items-start"
            >
              <div className="shrink-0 w-6 h-6 rounded-lg bg-purple-a8/15 text-purple-a8 flex items-center justify-center">
                <MessageCircle className="w-3.5 h-3.5" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-purple-a8 uppercase tracking-wide">
                  Question 1
                </p>
                <p className="text-xs font-semibold text-black-31 mt-0.5 leading-snug">
                  Why this university?
                </p>
              </div>
            </motion.div>

            {/* Bubble 2: Bottom-Right */}
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{
                repeat: Infinity,
                duration: 4.5,
                ease: "easeInOut",
                delay: 0.5,
              }}
              className="absolute right-2 bottom-8 bg-white border border-border rounded-2xl shadow-card px-4 py-3 max-w-[190px] flex gap-2 items-start"
            >
              <div className="shrink-0 w-6 h-6 rounded-lg bg-blue-ea text-blue-00! flex items-center justify-center">
                <Brain className="w-3.5 h-3.5" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-blue-00! uppercase tracking-wide">
                  Question 2
                </p>
                <p className="text-xs font-semibold text-black-31 mt-0.5 leading-snug">
                  Who is sponsoring your education?
                </p>
              </div>
            </motion.div>

            {/* Bubble 3: Top-Right */}
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{
                repeat: Infinity,
                duration: 3.5,
                ease: "easeInOut",
                delay: 1,
              }}
              className="absolute right-4 top-12 bg-white border border-border rounded-2xl shadow-card px-4 py-3 max-w-[170px] flex gap-2 items-start"
            >
              <div className="shrink-0 w-6 h-6 rounded-lg bg-amber/15 text-amber flex items-center justify-center">
                <Star className="w-3.5 h-3.5" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-amber uppercase tracking-wide">
                  Insight
                </p>
                <p className="text-xs font-semibold text-black-31 mt-0.5 leading-snug">
                  Be clear on your ties to home country
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default Hero;
